import { type FormEvent, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import {
  Button, Card, Empty, ErrorNote, Field, PageHeader, Spinner, TextArea, formatDate, useLoad,
} from "../components/ui"
import { NotReimportedList } from "../components/NotReimportedList"
import {
  type EmbedOutcome,
  type HubStats,
  type ImportResult,
  type Page,
  type SearchResult,
  type Space,
  type SpaceKind,
  api,
} from "../lib/api"

// The Context Hub: the company's own written knowledge, and what the agent
// reads to understand why the code is the way it is.

export function ContextHubPage() {
  const [params, setParams] = useSearchParams()
  const query = params.get("q") ?? ""
  const [creating, setCreating] = useState(false)
  const mode = params.get("mode") ?? "hybrid"
  const spaces = useLoad(() => api.get<{ spaces: Space[] }>("/context-hub/spaces"))
  const stats = useLoad(() => api.get<HubStats>("/context-hub/stats"))
  const results = useLoad(
    () =>
      query
        ? api.get<{ results: SearchResult[] }>(
            `/context-hub/search?q=${encodeURIComponent(query)}&mode=${mode}`,
          )
        : Promise.resolve({ results: [] }),
    [query, mode],
  )

  return (
    <>
      <PageHeader
        title="Context Hub"
        subtitle="Runbooks, decisions and architecture notes. The agent reads these too."
        action={<Button onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "New space"}</Button>}
      />

      {stats.data && stats.data.pages > 0 && <HubHealth stats={stats.data} />}

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          const next = String(new FormData(e.currentTarget).get("q") ?? "")
          setParams(next ? { q: next, mode } : {})
        }}
      >
        <div className="min-w-[16rem] flex-1">
          <Field label="Search" name="q" defaultValue={query} placeholder="Ask a question, or search for a term" />
        </div>
        <select
          value={mode}
          onChange={(e) => setParams(query ? { q: query, mode: e.target.value } : {})}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
          // hybrid is the default because keyword alone misses a page that says
          // the same thing in different words, and semantic alone misses an
          // exact identifier that appears once.
          title="Hybrid searches by meaning and by keyword. Keyword is better for an exact identifier."
        >
          <option value="hybrid">Meaning + keyword</option>
          <option value="semantic">Meaning only</option>
          <option value="keyword">Keyword only</option>
        </select>
      </form>

      {query && (
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold text-foreground">Results for “{query}”</h2>
          {results.loading ? (
            <Spinner />
          ) : results.data?.results.length ? (
            results.data.results.map((r) => (
              <Link key={r.pageId} to={`/context-hub/pages/${r.pageId}`}>
                <Card className="p-4 transition-colors hover:border-accent/40">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-medium text-foreground">{r.title}</p>
                    <span className="shrink-0 font-mono text-xs text-faint" title="Match score">
                      {r.score.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-faint">
                    {r.spaceName} · {r.path}
                    {r.headingPath.length > 0 && ` · ${r.headingPath.join(" › ")}`}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm text-muted">{r.snippet}</p>
                  {r.stale && (
                    // Documentation past its review date may be wrong, and an
                    // answer drawn from it should say so before it is trusted.
                    <p className="mt-2 text-xs text-warning">Past its review date — may be out of date</p>
                  )}
                </Card>
              </Link>
            ))
          ) : (
            <Empty
              title="Nothing matched"
              hint={
                mode === "keyword"
                  ? "Keyword search is literal. Try 'Meaning + keyword' to find pages phrased differently."
                  : "Nothing in the wiki covers this yet."
              }
            />
          )}
        </section>
      )}

      {creating && <CreateSpace onDone={() => (setCreating(false), spaces.reload())} />}

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-foreground">Spaces</h2>
        <ErrorNote error={spaces.error} />
        {spaces.loading ? (
          <Spinner />
        ) : spaces.data?.spaces.length ? (
          spaces.data.spaces.map((s) => (
            <Link key={s.id} to={`/context-hub/spaces/${s.id}`}>
              <Card className="flex items-center justify-between gap-4 p-4 transition-colors hover:border-accent/40">
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    {s.name}
                    {s.kind !== "manual" && (
                      <span className="ml-2 rounded border border-border px-1.5 py-0.5 text-xs font-normal text-muted">
                        {KIND_LABELS[s.kind]}
                      </span>
                    )}
                  </p>
                  {s.description && <p className="truncate text-sm text-muted">{s.description}</p>}
                </div>
                <span className="shrink-0 text-xs text-faint">
                  {s.lastImportedAt ? `synced ${formatDate(s.lastImportedAt)}` : formatDate(s.createdAt)}
                </span>
              </Card>
            </Link>
          ))
        ) : (
          <Empty title="No spaces yet" hint="A space groups related pages — usually one per system or team." />
        )}
      </section>
    </>
  )
}

// The token is written once and never read back — the API returns only a hint
// like "token ****ab12". Replacing it means typing a new one, which is correct:
// a credential you can retrieve from a web page is a credential that leaks
// through a screenshot.
/** The raw slug reads like a database value in the two places it is shown. */
const KIND_LABELS: Record<SpaceKind, string> = {
  manual: "written here",
  confluence: "Confluence",
  bookstack: "BookStack",
  "google-drive": "Google Drive",
}

/** Why a connect attempt came back unhappy, in words someone can act on. The
 *  codes are produced by the OAuth callback in src/google-drive/routes.ts. */
const CONNECT_ERRORS: Record<string, string> = {
  declined: "The Google sign-in was cancelled, so nothing was connected.",
  state_missing: "That sign-in link was not one we started. Try connecting again.",
  state_expired: "The Google sign-in took too long. Try connecting again.",
  no_refresh_token:
    "Google did not return a long-lived token. Remove Moderniser from the account's third-party access at myaccount.google.com and connect again.",
  token_exchange_failed: "Google refused to complete the sign-in. Try again.",
  not_configured: "Google Drive is not configured on this server yet — ask an administrator.",
}

function CredentialForm({
  space,
  onDone,
  onCancel,
}: {
  space: Space
  onDone: () => void
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // Google Drive has no credential to type. The token arrives from the consent
  // callback, so the whole form is one button that leaves for Google -- see the
  // comment above about why a credential you can read back is one that leaks.
  if (space.kind === "google-drive") {
    return (
      <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-muted p-4">
        <p className="text-sm text-muted">
          Connecting sends you to Google to approve read-only access. Moderniser then imports
          the folders listed on this space.
        </p>
        <p className="text-sm text-foreground">
          <strong>Use a Google account created for Moderniser</strong>, not your own. Every
          page imported here is readable by everyone in the company, so the connected account
          should only ever be given access to folders meant to be shared that widely — and a
          personal account stops working the day that person leaves.
        </p>
        <ErrorNote error={error} />
        <div className="flex gap-2">
          <Button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                const { url } = await api.get<{ url: string }>(
                  `/context-hub/spaces/${space.id}/google-drive/authorize-url`,
                )
                // A full-page navigation, not a popup: the consent screen
                // refuses to render in an iframe, and a popup is what gets
                // blocked. The callback brings the browser back here.
                window.location.href = url
              } catch (err) {
                setError(err)
                setBusy(false)
              }
            }}
          >
            {busy ? "Opening Google…" : space.credentialHint ? "Reconnect Google account" : "Connect Google account"}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-border bg-surface-muted p-4"
      onSubmit={async (e) => {
        e.preventDefault()
        const form = new FormData(e.currentTarget)
        setBusy(true)
        setError(null)
        try {
          const body =
            space.kind === "confluence"
              ? {
                  token: form.get("token"),
                  // Confluence Cloud authenticates with email + API token as
                  // basic auth; Server and Data Center use a bearer PAT and
                  // have no email. Which one is in play is decided by whether
                  // an email was given, not by parsing the URL.
                  ...(form.get("email") ? { email: form.get("email") } : {}),
                }
              : { tokenId: form.get("tokenId"), tokenSecret: form.get("tokenSecret") }

          await api.put(`/context-hub/spaces/${space.id}/credentials`, body)
          onDone()
        } catch (err) {
          setError(err)
        } finally {
          setBusy(false)
        }
      }}
    >
      {space.kind === "confluence" ? (
        <>
          <Field
            label="Email (Confluence Cloud only — leave blank for Server or Data Center)"
            name="email"
            placeholder="you@acme.com"
          />
          <Field label="API token" name="token" type="password" required />
        </>
      ) : (
        <>
          <Field label="Token ID" name="tokenId" required />
          <Field label="Token secret" name="tokenSecret" type="password" required />
        </>
      )}

      <ErrorNote error={error} />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save credential"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

// Re-sync is a button because the credential is already stored. There is no
// scheduler: the old platform ran three nightly jobs and needed a UI to
// configure and watch them, and storing the credential is what makes one click
// enough for a wiki that changes a few times a week.
// Embedding is a separate, billed step, and the UI says so before it is
// clicked. Creating a page and paying to vectorise it are different decisions:
// a page is browsable from the moment it exists and reachable by meaning only
// once someone asks for that.
function EmbedPanel({ space, onDone }: { space: Space; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [outcome, setOutcome] = useState<EmbedOutcome | null>(null)

  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-foreground">Search by meaning</p>
          <p className="text-sm text-muted">
            Indexes pages so a question phrased differently still finds them. Costs a small amount of
            credit, and only pages that are new or have changed since last time.
          </p>
        </div>
        <Button
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              setOutcome(await api.post<EmbedOutcome>(`/context-hub/spaces/${space.id}/embed`, {}))
              onDone()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? "Indexing…" : "Index for search"}
        </Button>
      </div>

      <ErrorNote error={error} />

      {outcome && (
        <div className="flex flex-col gap-1 text-sm text-muted">
          <p>
            {outcome.pagesEmbedded === 0 && outcome.pagesFailed === 0 && outcome.pagesSkipped === 0
              ? "Everything was already up to date — nothing was charged."
              : `${outcome.pagesEmbedded} pages indexed, ${outcome.chunksWritten} sections written` +
                (outcome.chunksReused > 0 ? `, ${outcome.chunksReused} unchanged and reused` : "")}
          </p>
          {outcome.pagesSkipped > 0 && (
            // Blocked, not failed: there is something for a person to fix, and
            // retrying without fixing it would fail the same way.
            <p className="text-warning">
              {outcome.pagesSkipped} skipped because they contain something that looks like a secret.
            </p>
          )}
          {outcome.pagesFailed > 0 && <p className="text-danger">{outcome.pagesFailed} failed.</p>}
          {outcome.errors.slice(0, 3).map((e) => (
            <p key={e} className="text-xs text-danger">
              {e}
            </p>
          ))}
        </div>
      )}
    </Card>
  )
}

// Coverage and health, shown only once there is something to be healthy about.
function HubHealth({ stats }: { stats: HubStats }) {
  const pct = stats.pages ? Math.round((stats.embedded / stats.pages) * 100) : 0
  return (
    <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-sm">
      <span className="text-muted">
        <span className="font-mono text-foreground">{stats.pages}</span> pages
      </span>
      <span className="text-muted">
        {/* Only embedded pages are reachable by meaning. The rest are
            browsable and invisible to a semantic search. */}
        <span className="font-mono text-foreground">{pct}%</span> searchable by meaning
      </span>
      {stats.drifted > 0 && (
        <span className="text-warning">{stats.drifted} edited since last indexed</span>
      )}
      {stats.stale > 0 && <span className="text-warning">{stats.stale} past review date</span>}
      {stats.blocked > 0 && <span className="text-danger">{stats.blocked} blocked by a secret</span>}
      {stats.failed > 0 && <span className="text-danger">{stats.failed} failed to index</span>}
    </Card>
  )
}

function SyncPanel({ space, onDone }: { space: Space; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [editingCredential, setEditingCredential] = useState(false)
  const [params, setParams] = useSearchParams()

  const hasCredential = Boolean(space.credentialHint)
  const isDrive = space.kind === "google-drive"
  // Set by the OAuth callback when it redirects the browser back here.
  const connectOutcome = params.get("googleDrive")
  const needsReconnect = Boolean((space.settings as { needsReconnect?: boolean })?.needsReconnect)

  // Read once and cleared, so a refresh does not replay a stale banner and the
  // URL stops carrying a parameter that has already been acted on.
  const dismissConnectOutcome = () => {
    const next = new URLSearchParams(params)
    next.delete("googleDrive")
    next.delete("reason")
    setParams(next, { replace: true })
  }

  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-foreground">Imported from {KIND_LABELS[space.kind]}</p>
          <p className="text-sm text-muted">
            {hasCredential
              ? `${isDrive ? "Connected as" : "Credential"} ${space.credentialHint} · ${
                  space.lastImportedAt
                    ? `last synced ${formatDate(space.lastImportedAt)} (${space.lastImportStatus})`
                    : "never synced"
                }`
              : isDrive
                ? "No Google account connected yet — connect one before this can sync."
                : "No credential stored yet — an admin must add one before this can sync."}
          </p>
        </div>
        <Button
          disabled={busy || !hasCredential}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              setResult(await api.post<ImportResult>(`/context-hub/spaces/${space.id}/import`, {}))
              onDone()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      <ErrorNote error={error} />

      {connectOutcome && (
        <button
          type="button"
          onClick={dismissConnectOutcome}
          className={`rounded-md border px-3 py-2 text-left text-sm ${
            connectOutcome === "connected"
              ? "border-border text-foreground"
              : "border-danger text-danger"
          }`}
        >
          {connectOutcome === "connected"
            ? "Google account connected. Sync now to import the folders on this space."
            : (CONNECT_ERRORS[params.get("reason") ?? ""] ?? "Connecting to Google failed.")}
        </button>
      )}

      {needsReconnect && (
        <p className="text-sm text-danger">
          Google has stopped honouring this connection — it was most likely revoked. Reconnect
          the account to resume syncing.
        </p>
      )}

      {space.kind === "bookstack" && (
        <ShelfPicker space={space} hasCredential={hasCredential} onDone={onDone} />
      )}

      {editingCredential ? (
        <CredentialForm
          space={space}
          onDone={() => {
            setEditingCredential(false)
            onDone()
          }}
          onCancel={() => setEditingCredential(false)}
        />
      ) : (
        <button
          type="button"
          className="self-start text-xs text-muted underline underline-offset-2 hover:text-foreground"
          onClick={() => setEditingCredential(true)}
        >
          {isDrive
            ? hasCredential
              ? "Reconnect Google account"
              : "Connect Google account"
            : hasCredential
              ? "Replace credential"
              : "Add credential"}
        </button>
      )}

      {result && (
        <p className="text-sm text-muted">
          {result.pagesCreated} added, {result.pagesUpdated} updated, {result.pagesUnchanged} unchanged
          {result.pagesSkipped > 0 && `, ${result.pagesSkipped} kept (edited here)`}
          {result.pagesRefused > 0 && `, ${result.pagesRefused} refused`}
          {result.filesUnsupported > 0 && `, ${result.filesUnsupported} of a type we do not read`}
          {result.imagesImported > 0 && `, ${result.imagesImported} image(s) copied`}
          {result.imagesFailed > 0 && `, ${result.imagesFailed} image(s) not copied`}
          {result.errors.length > 0 && ` — ${result.errors.length} problem(s)`}
        </p>
      )}
      {result && <NotReimportedList pages={result.notReimported} omitted={result.notReimportedOmitted} />}
      {space.lastImportMessage && !result && (
        <p className="text-sm text-muted">{space.lastImportMessage}</p>
      )}
    </Card>
  )
}

type BookstackShelf = { id: number; slug: string; name: string; books: number }

// Which shelves a BookStack space imports. Each shelf brings every book on it;
// none chosen means the whole wiki. The list is read from BookStack with the
// stored token, so it needs one first.
function ShelfPicker({ space, hasCredential, onDone }: { space: Space; hasCredential: boolean; onDone: () => void }) {
  // Spaces from the retired platform spell it shelf_slugs.
  const settings = (space.settings ?? {}) as { shelfSlugs?: string[]; shelf_slugs?: string[]; baseUrl?: string; base_url?: string }
  const current = (settings.shelfSlugs ?? settings.shelf_slugs ?? []).filter(Boolean)
  const [open, setOpen] = useState(false)
  const [shelves, setShelves] = useState<BookstackShelf[] | null>(null)
  const [chosen, setChosen] = useState<Set<string>>(new Set(current))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function load() {
    setOpen(true)
    setError(null)
    setChosen(new Set(current))
    try {
      const res = await api.get<{ shelves: BookstackShelf[] }>(`/context-hub/spaces/${space.id}/bookstack/shelves`)
      setShelves(res.shelves)
    } catch (e) {
      setError(e)
    }
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      // Slugs that are not shelves (book slugs typed into the form) are kept.
      const shelfSlugs = new Set(shelves?.map((s) => s.slug))
      const kept = current.filter((slug) => !shelfSlugs.has(slug))
      await api.patch(`/context-hub/spaces/${space.id}`, {
        settings: {
          ...(space.settings as Record<string, unknown>),
          ...(settings.baseUrl || !settings.base_url ? {} : { baseUrl: settings.base_url }),
          shelfSlugs: [...kept, ...chosen].filter((v, i, a) => a.indexOf(v) === i),
        },
      })
      setOpen(false)
      onDone()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  const toggle = (slug: string) =>
    setChosen((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted">
          {current.length ? `Shelves: ${current.join(", ")}` : "Shelves: all of the wiki"}
        </span>
        {hasCredential && !open && (
          <button
            type="button"
            className="text-xs text-muted underline underline-offset-2 hover:text-foreground"
            onClick={load}
          >
            Choose shelves
          </button>
        )}
      </div>

      {open && (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          {!shelves && !error && <Spinner />}
          {shelves && shelves.length === 0 && <p className="text-sm text-muted">This token sees no shelves.</p>}
          {shelves && shelves.length > 0 && (
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {shelves.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={chosen.has(s.slug)} onChange={() => toggle(s.slug)} />
                  <span className="text-foreground">{s.name}</span>
                  <span className="text-xs text-muted">
                    {s.slug} · {s.books} book{s.books === 1 ? "" : "s"}
                  </span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted">
            Every book on a chosen shelf is imported. Choosing none imports the whole wiki.
          </p>
          <ErrorNote error={error} />
          <div className="flex gap-2">
            <Button disabled={busy || !shelves} onClick={save}>{busy ? "Saving…" : "Save shelves"}</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  )
}

// A space is also the source its pages come from, so this form has to be able
// to say which system that is. Without the kind and its settings, a connector
// space could only be created by hand against the API — and then the sync
// button below is permanently disabled, which is where this started.
function CreateSpace({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [kind, setKind] = useState<SpaceKind>("manual")

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setBusy(true)
    setError(null)

    // Comma-separated in the form, an array over the wire. Empty means "the
    // whole instance", which the importer already treats as no filter.
    const list = (name: string) =>
      String(form.get(name) ?? "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)

    const settings =
      kind === "confluence"
        ? { baseUrl: form.get("baseUrl"), spaceKeys: list("spaceKeys") }
        : kind === "bookstack"
          ? { baseUrl: form.get("baseUrl"), shelfSlugs: list("shelfSlugs") }
          : kind === "google-drive"
            ? { folderIds: list("folderIds") }
            : {}

    const days = String(form.get("reviewAfterDays") ?? "").trim()

    try {
      await api.post("/context-hub/spaces", {
        name: form.get("name"),
        description: form.get("description") || undefined,
        kind,
        settings,
        // Null rather than omitted: null is "never expires", which is the
        // honest default when nobody has an opinion.
        reviewAfterDays: days ? Number(days) : null,
      })
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-5">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Name" name="name" placeholder="Engineering" required />
        <Field label="Description" name="description" />

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted">Where the pages come from</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as SpaceKind)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
          >
            <option value="manual">Written here, or by the agent</option>
            <option value="confluence">Imported from Confluence</option>
            <option value="bookstack">Imported from BookStack</option>
            <option value="google-drive">Imported from Google Drive</option>
          </select>
        </label>

        {kind === "google-drive" && (
          <>
            <Field
              label="Folder IDs, comma separated"
              name="folderIds"
              placeholder="1A2b3C4dEfGhIjKlMnOp"
              required
            />
            <p className="text-xs text-muted">
              A folder ID is the last part of its Drive URL. Only these folders and their
              subfolders are imported — naming the whole Drive would import, embed and bill
              every file the connected account can see.
            </p>
            <p className="text-xs text-muted">
              Google Docs, Sheets, PDFs, Word, Excel, PowerPoint, Markdown, text and CSV are
              read. Anything else is skipped and counted.
            </p>
            <p className="text-xs text-muted">
              You will connect a Google account after creating the space. Nothing is fetched
              until you do.
            </p>
          </>
        )}

        {kind !== "manual" && kind !== "google-drive" && (
          <>
            <Field
              label="Base URL"
              name="baseUrl"
              placeholder={
                kind === "confluence" ? "https://acme.atlassian.net/wiki" : "https://wiki.acme.com"
              }
              required
            />
            {kind === "confluence" ? (
              <Field
                label="Space keys, comma separated (blank for all)"
                name="spaceKeys"
                placeholder="ENG, OPS"
              />
            ) : (
              <>
                <Field
                  label="Shelf slugs, comma separated (blank for all)"
                  name="shelfSlugs"
                  placeholder="engineering, runbooks"
                />
                <p className="text-xs text-muted">
                  Every book on these shelves is imported. Book slugs work too. Once the credential
                  is added you can also pick shelves from a list.
                </p>
              </>
            )}
            <p className="text-xs text-muted">
              You will add the credential after creating the space. Nothing is fetched until you do.
            </p>
          </>
        )}

        <Field
          label="Review pages after (days, blank for never)"
          name="reviewAfterDays"
          placeholder="180"
        />

        <ErrorNote error={error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>{busy ? "Creating…" : "Create space"}</Button>
          <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
        </div>
      </form>
    </Card>
  )
}

export function SpacePage() {
  const { spaceId = "" } = useParams()
  const [creating, setCreating] = useState(false)
  const space = useLoad(() => api.get<{ space: Space }>(`/context-hub/spaces/${spaceId}`), [spaceId])
  const pages = useLoad(() => api.get<{ pages: Page[] }>(`/context-hub/spaces/${spaceId}/pages`), [spaceId])

  return (
    <>
      <PageHeader
        title={space.data?.space.name ?? "Pages"}
        subtitle={space.data?.space.description ?? undefined}
        action={<Button onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "New page"}</Button>}
      />

      {space.data?.space && space.data.space.kind !== "manual" && (
        <SyncPanel space={space.data.space} onDone={() => (space.reload(), pages.reload())} />
      )}

      {space.data?.space && <EmbedPanel space={space.data.space} onDone={pages.reload} />}
      {creating && (
        <Card className="p-5">
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const form = new FormData(e.currentTarget)
              await api.post(`/context-hub/spaces/${spaceId}/pages`, {
                title: form.get("title"), content: form.get("content"),
              })
              setCreating(false); pages.reload()
            }}
            className="flex flex-col gap-4"
          >
            <Field label="Title" name="title" required />
            <TextArea label="Content" name="content" hint="Markdown." required />
            <div className="flex gap-2">
              <Button type="submit">Create page</Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}
      <ErrorNote error={pages.error} />
      {pages.loading ? <Spinner /> : pages.data?.pages.length ? (
        <div className="flex flex-col gap-2">
          {pages.data.pages.map((p) => (
            <Link key={p.id} to={`/context-hub/pages/${p.id}`}>
              <Card className="flex items-center justify-between gap-4 p-4 transition-colors hover:border-accent/40">
                <p className="truncate font-medium text-foreground">{p.title}</p>
                <span className="shrink-0 text-xs text-faint">
                  {p.sourceRef ? `imported · ${p.path}` : formatDate(p.updatedAt)}
                </span>
              </Card>
            </Link>
          ))}
        </div>
      ) : <Empty title="No pages in this space yet" />}
    </>
  )
}

// What a reader needs before trusting the page: whether it is past review,
// whether the agent can find it by meaning, and whether it holds a secret.

